import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Warehouse, CheckCircle2, Users as UsersIcon, Building2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { SearchInput } from "@/components/kit/SearchInput";
import { FilterShell } from "@/components/kit/FilterShell";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { DataTable } from "@/components/tables/DataTable";
import { RowActions } from "@/components/tables/RowActions";
import { EmptyState } from "@/components/feedback/EmptyState";
import { TableSkeleton } from "@/components/feedback/Skeletons";
import { ConfirmDialog } from "@/components/feedback/ConfirmDialog";
import { WarehouseDrawer } from "./WarehouseDrawer";
import { warehousesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";

const DEFAULT_FILTERS = { search: "", status: "all" };

export default function WarehousesPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const params = useMemo(
    () => ({ search: applied.search, status: applied.status === "all" ? "" : applied.status }),
    [applied]
  );

  const listQuery = useQuery({ queryKey: ["warehouses", params], queryFn: () => warehousesService.list(params) });
  const summaryQuery = useQuery({ queryKey: ["warehouses", "summary"], queryFn: warehousesService.summary });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["warehouses"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
  };

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (row) => { setEditing(row); setDrawerOpen(true); };

  const confirmDelete = async () => {
    try {
      await warehousesService.remove(toDelete.id);
      toast.success("Warehouse deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setToDelete(null);
    }
  };

  const s = summaryQuery.data || {};
  const rows = listQuery.data?.items || [];

  const columns = [
    {
      key: "name",
      header: "Warehouse",
      render: (r) => (
        <div>
          <p className="font-medium text-foreground">{r.name}</p>
          {r.code ? <p className="text-xs text-muted-foreground">{r.code}</p> : null}
        </div>
      ),
    },
    {
      key: "contact",
      header: "Contact",
      render: (r) =>
        r.contact_name || r.contact_email ? (
          <div>
            <p className="text-sm">{r.contact_name || "—"}</p>
            <p className="text-xs text-muted-foreground">{r.contact_email}</p>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    { key: "users", header: "Users", render: (r) => r.users },
    { key: "clients", header: "Clients", render: (r) => r.clients },
    { key: "stores", header: "Stores", render: (r) => r.stores },
    {
      key: "status",
      header: "Status",
      render: (r) => <StatusBadge label={r.status === "active" ? "Active" : "Inactive"} tone={r.status === "active" ? "green" : "neutral"} />,
    },
    { key: "created", header: "Created", render: (r) => formatRelativeTime(r.created_at) },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        <RowActions
          items={[
            { key: "edit", label: "Edit", icon: Pencil, onSelect: () => openEdit(r) },
            { key: "delete", label: "Delete", icon: Trash2, destructive: true, separatorBefore: true, onSelect: () => setToDelete(r) },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="page-warehouses">
      <PageHeader
        sticky
        title="Warehouses"
        description="Onboard warehouse (3PL) organizations. Each warehouse only sees its own clients, stores and operations."
        actions={
          <Button className="gap-2" onClick={openCreate} data-testid="warehouses-primary-action">
            <Plus className="h-4 w-4" />
            New Warehouse
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-total-warehouses" label="Total Warehouses" value={s.total ?? "—"} icon={Warehouse} tone="blue" />
        <StatCard testId="stat-active-warehouses" label="Active" value={s.active ?? "—"} icon={CheckCircle2} tone="green" />
        <StatCard testId="stat-warehouse-users" label="Warehouse Users" value={s.users ?? "—"} icon={UsersIcon} tone="amber" />
        <StatCard testId="stat-warehouse-clients" label="Clients Onboarded" value={s.clients ?? "—"} icon={Building2} tone="neutral" />
      </div>

      <FilterShell
        onApply={() => { setApplied(draft); toast.success("Filters applied"); }}
        onReset={() => { setDraft(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); toast("Filters reset"); }}
      >
        <SearchInput value={draft.search} onChange={(v) => setDraft((d) => ({ ...d, search: v }))} placeholder="Search warehouses…" />
        <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </FilterShell>

      {listQuery.isLoading ? (
        <TableSkeleton rows={5} cols={7} />
      ) : (
        <DataTable
          testId="warehouses-table"
          paginated
          columns={columns}
          data={rows}
          emptyState={
            <EmptyState
              icon={Warehouse}
              title="No warehouses yet"
              description="Onboard your first warehouse (3PL) organization, then invite its 3PL Owner."
              testId="warehouses-empty-state"
              action={
                <Button className="gap-2" onClick={openCreate} data-testid="warehouses-empty-action">
                  <Plus className="h-4 w-4" /> New Warehouse
                </Button>
              }
            />
          }
        />
      )}

      <WarehouseDrawer open={drawerOpen} onOpenChange={setDrawerOpen} record={editing} onSaved={refresh} />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete warehouse?"
        description={`This permanently removes "${toDelete?.name}". Warehouses with users, clients or stores cannot be deleted.`}
        confirmLabel="Delete warehouse"
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  );
}
